// ApplicationsView — the "Aplicaciones" module (main nav).
//
// Two sub-tabs:
//   • Plantillas — manage the agency's application templates (bilingual,
//     per policy type): toggle sections, edit custom questions, intro, active.
//   • Enviadas — list of applications sent + their status, with the link.
// Also lets you SEND an application to any recipient (no quote required).
import React, { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Send, FileText, Edit3, Copy, Check, X, Loader2, AlertCircle, Power, Eye, Upload, RefreshCw, Ban } from "lucide-react";
import {
  listApplicationTemplates, createApplicationTemplate, updateApplicationTemplate,
  deleteApplicationTemplate, listApplications, sendApplication, deleteApplication, resendApplication, revokeApplication,
  getApplicationSubmission, aiApplicationFromDoc,
} from "../api.js";
import { copyText } from "../utils.js";
import { ClientSearchSelect } from "./ui.jsx";
import { filaDeDestinatario, vincularDestinatario, destinatariosQueSeEnvian } from "../destinatariosDeAplicacion.js";
import { espejoTieneAlgoQueRepetir } from '../applicationMirror.js';
import { SECCIONES_DE_AUTO, CLAVES_DE_AUTO, CAMPOS_CON_LA_DIRECCION, etiqueta, camposParaVer, viviendaVaConLaDireccion } from '../formularioDeAuto.js';
import { ocrPdfDoc, hayTextoSuficiente } from '../ocrPdf.js';

// El OCR de un PDF escaneado vive fuera desde el 2026-09-08: el importador
// de aplicaciones necesita exactamente el mismo camino para leer una
// planilla pasada por el escaner. Una sola copia, no dos que se desvian.
// Ver crm-app/ocrPdf.js.

// Extract plain text from an uploaded PDF / Word file. PDF.js + mammoth are
// lazy-loaded from the SRI-pinned cdnVendor registry (same as SignaturesView /
// ImportView), so agents who never import a doc pay no bundle cost.
// `onProgress(page, total)` is called only while OCR is running.
async function extractDocText(file, onProgress) {
  const name = (file.name || "").toLowerCase();
  const isDocx = name.endsWith(".docx") || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (isDocx) {
    if (!window.mammoth) {
      const { loadCdnScript, MAMMOTH_1_8_0 } = await import("../cdnVendor.js");
      await loadCdnScript(MAMMOTH_1_8_0);
    }
    const buf = await file.arrayBuffer();
    const raw = await window.mammoth.extractRawText({ arrayBuffer: buf });
    return (raw && raw.value) || "";
  }
  if (!window.pdfjsLib) {
    const { loadCdnScript, PDFJS_3_11_174, PDFJS_WORKER_3_11_174 } = await import("../cdnVendor.js");
    await loadCdnScript(PDFJS_3_11_174);
    await loadCdnScript(PDFJS_WORKER_3_11_174);
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_3_11_174.url;
  }
  const buf = await file.arrayBuffer();
  // Keep this ONE document object for both passes: pdf.js detaches the
  // ArrayBuffer it is handed, so re-reading `buf` for OCR would give it an
  // empty buffer.
  const pdf = await window.pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
  let text = "";
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map((it) => it.str).join(" ") + "\n";
  }
  if (hayTextoSuficiente(text)) return text;
  return await ocrPdfDoc(pdf, onProgress);
}

const POLICY_TYPES = [
  ["", { es: "General", en: "General" }],
  ["HEALTH", { es: "Salud", en: "Health" }],
  ["LIFE", { es: "Vida", en: "Life" }],
  ["AUTO", { es: "Auto", en: "Auto" }],
  ["HOME", { es: "Hogar", en: "Home" }],
  ["DENTAL", { es: "Dental", en: "Dental" }],
  ["VISION", { es: "Visión", en: "Vision" }],
  ["MEDICARE_ADVANTAGE", { es: "Medicare Advantage", en: "Medicare Advantage" }],
  ["MEDICARE_SUPPLEMENT", { es: "Medicare Supplement", en: "Medicare Supplement" }],
  ["DISABILITY", { es: "Incapacidad", en: "Disability" }],
  ["ANNUITY", { es: "Anualidad", en: "Annuity" }],
  ["COMMERCIAL", { es: "Comercial", en: "Commercial" }],
  ["RENTERS", { es: "Inquilinos", en: "Renters" }],
  ["FLOOD", { es: "Inundación", en: "Flood" }],
  ["UMBRELLA", { es: "Paraguas", en: "Umbrella" }],
  ["PET", { es: "Mascotas", en: "Pet" }],
  ["TRAVEL", { es: "Viaje", en: "Travel" }],
  ["WORKERS_COMP", { es: "Workers' Comp", en: "Workers' Comp" }],
  ["LONG_TERM_CARE", { es: "Cuidado a largo plazo", en: "Long-term care" }],
];

const SECTIONS = [
  ["address", { es: "Dirección", en: "Address" }],
  // Dos casillas, no una (Lissette 2026-09-04, punto 67): pedir la fecha de
  // nacimiento no obliga a pedir tambien el numero de seguro social, que es
  // el dato mas delicado del formulario.
  ["dob", { es: "Fecha de nacimiento", en: "Date of birth" }],
  ["ssn", { es: "SSN / ITIN", en: "SSN / ITIN" }],
  ["spouse", { es: "Cónyuge", en: "Spouse" }],
  ["dependents", { es: "Dependientes", en: "Dependents" }],
  ["health", { es: "Salud", en: "Health" }],
  ["employment", { es: "Empleo y estado", en: "Employment & status" }],
];

const FIELD_TYPES = ["text", "textarea", "email", "phone", "number", "date", "select", "radio", "checkbox", "yesno"];
// Friendly bilingual labels for the field-type dropdown ([es, en]).
const FIELD_TYPE_LABELS = {
  text: ["Texto", "Text"], textarea: ["Texto largo", "Long text"], email: ["Correo", "Email"],
  phone: ["Teléfono", "Phone"], number: ["Número", "Number"], date: ["Fecha", "Date"],
  select: ["Lista desplegable", "Dropdown"], radio: ["Opción única (círculos)", "Single choice (radios)"],
  checkbox: ["Casilla (una)", "Checkbox (one)"], yesno: ["Sí / No (dos casillas)", "Yes / No (two boxes)"],
};

export function ApplicationsView({ lang = "es", clients = [], clientsLoaded = false, clientsServerTotal = null }) {
  const tt = (es, en) => (lang === "es" ? es : en);
  const ptLabel = (pt) => (POLICY_TYPES.find(([v]) => v === (pt || ""))?.[1]?.[lang]) || pt || tt("General", "General");

  const [tab, setTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // template object or {__new:true}
  const [showSend, setShowSend] = useState(false);
  const [previewTpl, setPreviewTpl] = useState(null); // template to preview blank-form
  const [importing, setImporting] = useState(false); // PDF/Word → template in progress
  const [importMsg, setImportMsg] = useState(""); // OCR progress ("Leyendo página 2 de 5…")
  const fileRef = useRef(null);

  // Import a PDF/Word application form → AI turns it into a template structure →
  // open the editor prefilled so the agent reviews/edits BEFORE saving.
  async function handleImportDoc(file) {
    if (!file) return;
    setError("");
    setImportMsg("");
    setImporting(true);
    try {
      // The OCR pass only runs for scanned PDFs, and it is slow enough that
      // silence reads as a hang — report the page it is on.
      const text = await extractDocText(file, (page, total) => setImportMsg(
        tt(`Documento escaneado: leyendo con OCR, página ${page} de ${total}…`,
           `Scanned document: reading with OCR, page ${page} of ${total}…`),
      ));
      if (!text || !text.trim()) {
        setError(tt("No se pudo leer texto del archivo, ni siquiera con OCR. Prueba con un PDF de mejor calidad o con el Word original.", "Could not read text from the file, not even with OCR. Try a higher-quality PDF or the original Word file."));
        return;
      }
      setImportMsg(tt("Analizando el formulario…", "Analyzing the form…"));
      const draft = await aiApplicationFromDoc({ text: text.slice(0, 20000), lang, title: file.name || "" });
      setEditing({
        __new: true,
        name: draft.name || "",
        policyType: draft.policyType || "",
        language: lang,
        intro: draft.intro || "",
        sections: {
          address: !!draft.sections?.address,
          dob: !!draft.sections?.dob, ssn: !!draft.sections?.ssn,
          spouse: !!draft.sections?.spouse, dependents: !!draft.sections?.dependents,
          health: !!draft.sections?.health, employment: !!draft.sections?.employment,
          mirror: !!draft.sections?.mirror,
        },
        customFields: Array.isArray(draft.customFields) ? draft.customFields : [],
        isActive: true,
        __imported: true,
      });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setImporting(false);
      setImportMsg("");
    }
  }

  async function refresh() {
    setLoading(true); setError("");
    try {
      const [tpls, list] = await Promise.all([listApplicationTemplates(), listApplications()]);
      setTemplates(tpls); setApps(list);
    } catch (err) { setError(err.message || String(err)); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  const C = {
    text: "var(--c-white,#eef6ff)", text2: "var(--c-text2,#7aa3c0)",
    card: "var(--ci-bg-04)", border: "var(--ci-bd-20)",
    accent: "#00d4ff",
  };
  const btnPrimary = { padding: ".6rem 1.1rem", borderRadius: 10, background: "linear-gradient(135deg,#00d4ff,#00a8c8)", color: "#0a1628", border: "none", cursor: "pointer", fontWeight: 700, fontSize: ".86rem", display: "inline-flex", alignItems: "center", gap: 6 };
  const btnGhost = { padding: ".55rem 1rem", borderRadius: 10, background: "var(--ci-bg-06)", color: C.text, border: `1px solid ${C.border}`, cursor: "pointer", fontWeight: 600, fontSize: ".84rem", display: "inline-flex", alignItems: "center", gap: 6 };

  return (
    <div style={{ padding: "1.25rem 1.5rem", color: C.text, maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: ".75rem", marginBottom: "1rem" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.4rem" }}>📝 {tt("Aplicaciones", "Applications")}</h2>
          <div style={{ fontSize: ".82rem", color: C.text2, marginTop: 2 }}>
            {tt("Plantillas de aplicación por tipo de póliza (bilingües y editables). Envía el formulario a cualquier cliente, con o sin cotización.",
                "Application templates by policy type (bilingual & editable). Send the form to any client, with or without a quote.")}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={btnGhost} onClick={() => setShowSend(true)}><Send size={15} /> {tt("Enviar aplicación", "Send application")}</button>
          <input ref={fileRef} type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" style={{ display: "none" }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleImportDoc(f); }} />
          <button style={{ ...btnGhost, opacity: importing ? .7 : 1, cursor: importing ? "wait" : "pointer" }} disabled={importing} title={tt("Sube un PDF o Word y la IA lo convierte en plantilla", "Upload a PDF or Word and AI turns it into a template")} onClick={() => fileRef.current && fileRef.current.click()}>
            {importing ? <Loader2 size={15} className="dkt-spin" /> : <Upload size={15} />} {importing ? tt("Convirtiendo…", "Converting…") : tt("Importar PDF/Word", "Import PDF/Word")}
          </button>
          <button style={btnPrimary} onClick={() => setEditing({ __new: true, name: "", policyType: "", language: lang, intro: "", sections: { address: true, dob: true, ssn: true, spouse: false, dependents: false, health: false, employment: false, mirror: false }, customFields: [], isActive: true })}><Plus size={16} /> {tt("Nueva plantilla", "New template")}</button>
        </div>
      </div>

      {error && (
        <div style={{ background: "#3a1a1a", border: "1px solid #ef4444", borderRadius: 10, padding: ".7rem 1rem", marginBottom: ".75rem", color: "#fca5a5", display: "flex", alignItems: "center", gap: 10, fontSize: ".86rem" }}>
          <AlertCircle size={16} /><span>{error}</span>
        </div>
      )}

      {importing && importMsg && (
        <div style={{ background: "rgba(0,212,255,.07)", border: `1px solid ${C.accent}`, borderRadius: 10, padding: ".7rem 1rem", marginBottom: ".75rem", color: C.text2, display: "flex", alignItems: "center", gap: 10, fontSize: ".86rem" }}>
          <Loader2 size={16} className="dkt-spin" /><span>{importMsg}</span>
        </div>
      )}

      {/* sub-tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: "1rem", borderBottom: `1px solid ${C.border}` }}>
        {[["templates", tt("Plantillas", "Templates")], ["sent", tt("Enviadas", "Sent")]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ padding: ".6rem 1rem", background: "none", border: "none", borderBottom: tab === id ? `2px solid ${C.accent}` : "2px solid transparent", color: tab === id ? C.accent : C.text2, cursor: "pointer", fontWeight: 700, fontSize: ".9rem" }}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: C.text2 }}><Loader2 size={20} className="dkt-spin" /> {tt("Cargando…", "Loading…")}</div>
      ) : tab === "templates" ? (
        templates.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2.5rem", color: C.text2 }}>{tt("No hay plantillas todavía.", "No templates yet.")}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "1rem" }}>
            {templates.map((tpl) => (
              <div key={tpl.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: "1rem" }}>{tpl.name}</div>
                  <span style={{ fontSize: ".66rem", fontWeight: 700, textTransform: "uppercase", background: tpl.isActive ? "rgba(0,212,255,.15)" : "var(--ci-bd-15)", color: tpl.isActive ? C.accent : C.text2, borderRadius: 6, padding: "2px 7px" }}>{tpl.isActive ? tt("Activa", "Active") : tt("Inactiva", "Off")}</span>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: ".72rem", background: "var(--ci-bg-06)", borderRadius: 6, padding: "2px 8px", color: C.text2 }}>{ptLabel(tpl.policyType)}</span>
                  <span style={{ fontSize: ".72rem", background: "var(--ci-bg-06)", borderRadius: 6, padding: "2px 8px", color: C.text2 }}>{tpl.language === "en" ? "EN" : "ES"}</span>
                  {tpl.isSeed && <span style={{ fontSize: ".72rem", color: C.text2 }}>· {tt("predeterminada", "default")}</span>}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button style={{ ...btnGhost, flex: 1, justifyContent: "center" }} onClick={() => setEditing(tpl)}><Edit3 size={14} /> {tt("Editar", "Edit")}</button>
                  <button style={{ ...btnGhost, padding: ".55rem .7rem" }} title={tt("Vista previa del formulario", "Preview form")} onClick={() => setPreviewTpl(tpl)}><Eye size={14} /></button>
                  <button style={{ ...btnGhost, padding: ".55rem .7rem" }} title={tt("Enviar con esta plantilla", "Send with this template")} onClick={() => setShowSend(tpl)}><Send size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <SentList apps={apps} lang={lang} ptLabel={ptLabel} C={C} canResend onDelete={async (id) => { try { await deleteApplication(id); await refresh(); } catch (e) { setError(e.message || String(e)); } }} />
      )}

      {editing && (
        <TemplateEditor
          lang={lang} initial={editing} C={C} ptLabel={ptLabel}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await refresh(); }}
          onError={setError}
        />
      )}
      {showSend && (
        <SendModal
          lang={lang} templates={templates} preselect={showSend && showSend.id ? showSend : null} C={C} ptLabel={ptLabel}
          clients={clients} clientsLoaded={clientsLoaded} clientsServerTotal={clientsServerTotal}
          onClose={() => setShowSend(false)}
          onSent={async () => { setShowSend(false); setTab("sent"); await refresh(); }}
          onError={setError}
        />
      )}
      {previewTpl && (
        <ApplicationPreview template={previewTpl} uiLang={lang} C={C} onClose={() => setPreviewTpl(null)} />
      )}
    </div>
  );
}

// ── Template editor ───────────────────────────────────────────────────
function TemplateEditor({ lang, initial, C, onClose, onSaved, onError }) {
  const tt = (es, en) => (lang === "es" ? es : en);
  const isNew = !!initial.__new;
  const [name, setName] = useState(initial.name || "");
  const [policyType, setPolicyType] = useState(initial.policyType || "");
  const [language, setLanguage] = useState(initial.language || lang);
  const [intro, setIntro] = useState(initial.intro || "");
  // `dobSsn` es la casilla UNICA de antes del 2026-09-04 (punto 67). El
  // servidor ya la reparte al listar las plantillas, asi que normalmente aqui
  // llegan `dob` y `ssn` hechos. Esto es la red por debajo: el frontend se
  // despliega unos MINUTOS ANTES que el backend, y en esa ventana esta
  // pantalla habla con un servidor que todavia manda la forma vieja. Sin la
  // red, en esos minutos el editor abriria las dos casillas apagadas.
  const [sections, setSections] = useState(() => {
    const ini = initial.sections || {};
    const viejo = ini.dobSsn !== false;
    return {
      address: true, spouse: false, dependents: false, health: false, employment: false, mirror: false,
      ...ini,
      dob: ini.dob !== undefined ? !!ini.dob : viejo,
      ssn: ini.ssn !== undefined ? !!ini.ssn : viejo,
    };
  });
  const [fields, setFields] = useState(() => (Array.isArray(initial.customFields) ? initial.customFields.map((f) => ({ ...f })) : []));
  const [isActive, setIsActive] = useState(initial.isActive !== false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const inputSt = { width: "100%", padding: ".55rem .7rem", borderRadius: 8, border: `1px solid ${C.border}`, background: "var(--ci-bg-06)", color: C.text, fontSize: ".88rem", boxSizing: "border-box" };
  const labelSt = { fontSize: ".78rem", fontWeight: 600, color: C.text2, display: "block", marginBottom: 4 };

  const addField = () => setFields((a) => [...a, { id: `cf_${Date.now().toString(36)}_${a.length}`, label: "", type: "text", required: false, options: [] }]);
  const setField = (i, patch) => setFields((a) => a.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const delField = (i) => setFields((a) => a.filter((_, j) => j !== i));

  async function save() {
    if (!name.trim()) { onError(tt("Ponle un nombre a la plantilla.", "Give the template a name.")); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), policyType: policyType || null, language, intro: intro.trim() || null,
        sections, isActive,
        customFields: fields.filter((f) => f.label.trim()).map((f) => ({
          id: f.id, label: f.label.trim(), type: f.type, required: !!f.required,
          options: ["select", "radio", "checkbox"].includes(f.type) ? (f.options || []).map((s) => s.trim()).filter(Boolean) : undefined,
        })),
      };
      if (isNew) await createApplicationTemplate(payload);
      else await updateApplicationTemplate(initial.id, payload);
      await onSaved();
    } catch (err) { onError(err.message || String(err)); }
    finally { setSaving(false); }
  }
  async function remove() {
    if (!window.confirm(tt(`¿Eliminar la plantilla "${name}"?`, `Delete template "${name}"?`))) return;
    setSaving(true);
    try { await deleteApplicationTemplate(initial.id); await onSaved(); }
    catch (err) { onError(err.message || String(err)); setSaving(false); }
  }

  return (
    <Modal onClose={onClose} C={C} title={isNew ? tt("Nueva plantilla", "New template") : tt("Editar plantilla", "Edit template")}>
      {initial.__imported && (
        <div style={{ background: "rgba(0,212,255,.10)", border: `1px solid ${C.border}`, borderRadius: 10, padding: ".6rem .85rem", fontSize: ".82rem", color: C.text, marginBottom: 12, display: "flex", gap: 8, alignItems: "flex-start" }}>
          <span style={{ fontSize: "1rem", lineHeight: 1 }}>✨</span>
          <span>{tt("Generado desde tu documento. Revisa las secciones y preguntas, ajusta lo que haga falta y luego Guardar.",
                   "Generated from your document. Review the sections and questions, adjust anything needed, then Save.")}</span>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ gridColumn: "1 / -1" }}><label style={labelSt}>{tt("Nombre", "Name")}</label><input style={inputSt} value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div><label style={labelSt}>{tt("Tipo de póliza", "Policy type")}</label><select style={inputSt} value={policyType} onChange={(e) => setPolicyType(e.target.value)}>{POLICY_TYPES.map(([v, l]) => <option key={v || "general"} value={v}>{l[lang]}</option>)}</select></div>
        <div><label style={labelSt}>{tt("Idioma", "Language")}</label><select style={inputSt} value={language} onChange={(e) => setLanguage(e.target.value)}><option value="es">Español</option><option value="en">English</option></select></div>
        <div style={{ gridColumn: "1 / -1" }}><label style={labelSt}>{tt("Texto de introducción", "Intro text")}</label><textarea rows={2} style={{ ...inputSt, resize: "vertical", fontFamily: "inherit" }} value={intro} onChange={(e) => setIntro(e.target.value)} /></div>
      </div>

      <div style={{ marginTop: 16 }}>
        <label style={labelSt}>{tt("Secciones a pedir", "Sections to ask")}</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {SECTIONS.map(([key, l]) => (
            <label key={key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: ".84rem", color: C.text, background: "var(--ci-bg-04)", border: `1px solid ${C.border}`, borderRadius: 8, padding: ".4rem .7rem", cursor: "pointer" }}>
              <input type="checkbox" checked={!!sections[key]} onChange={(e) => setSections((s) => ({ ...s, [key]: e.target.checked }))} /> {l[lang]}
            </label>
          ))}
        </div>

        {/* LAS SECCIONES DE AUTO (Lissette 2026-09-04, punto 69). En su propio
            grupo y no mezcladas con las de arriba: son siete y solo tienen
            sentido en una plantilla de auto, asi que puestas en la misma fila
            que "Dirección" o "Salud" convertirian el cuadro en una pared de
            casillas. La lista sale del MISMO catalogo que pinta el formulario
            (crm-app/formularioDeAuto.js), asi que no puede existir una casilla
            aqui que no exista alli.

            Y SOLO EN UNA PLANTILLA DE AUTO (Lissette, 2026-09-12: *"cuando te
            dije que actualizaras la aplicacion de carro, cambiaste todas las
            aplicaciones salud, dental, vision, le pusiste todo lo que es de
            carro"*). El grupo se dibujaba en TODAS las plantillas sin mirar el
            tipo, asi que abrir "Aplicacion de Salud" enseñaba siete casillas de
            carro. Estaban apagadas y el cliente nunca vio una pregunta de carro
            —el formulario publico y la vista previa pintan cada bloque solo si
            su casilla esta encendida, y solo la plantilla de Auto las trae
            asi—, pero eso no es consuelo: la pantalla decia otra cosa, y una
            pantalla que dice algo que no es cierto sobre datos de clientes se
            cree.

            La segunda condicion NO es de adorno: si una plantilla que no es de
            auto tuviera alguna de estas encendidas, esconder el grupo la
            dejaria encendida PARA SIEMPRE, sin ningun sitio donde apagarla.
            Nunca se esconde el unico control de algo que esta activo. */}
        {(policyType === "AUTO" || CLAVES_DE_AUTO.some((k) => sections[k])) && (
        <div style={{ marginTop: 14 }}>
          <label style={labelSt}>{tt("Formulario de auto", "Auto form")}</label>
          {policyType !== "AUTO" && (
            <div style={{ marginBottom: 8, padding: ".55rem .7rem", borderRadius: 9, background: "rgba(245,158,11,.1)", border: "1px solid rgba(245,158,11,.35)", color: "#b45309", fontSize: ".79rem", lineHeight: 1.45 }}>
              <strong>{tt("Esta plantilla no es de auto y tiene preguntas de auto encendidas.", "This template isn't an auto template and has auto questions turned on.")}</strong>{" "}
              {tt("Se muestran aquí para que puedas apagarlas. Mientras estén encendidas, el cliente las verá en el formulario.",
                  "They're shown here so you can turn them off. While they're on, the client sees them in the form.")}
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {SECCIONES_DE_AUTO.map((sec) => (
              <label key={sec.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: ".84rem", color: C.text, background: "var(--ci-bg-04)", border: `1px solid ${C.border}`, borderRadius: 8, padding: ".4rem .7rem", cursor: "pointer" }}>
                <input type="checkbox" checked={!!sections[sec.key]} onChange={(e) => setSections((sc) => ({ ...sc, [sec.key]: e.target.checked }))} />
                {etiqueta(sec, lang)}
                {sec.repetidor && <span style={{ color: C.text2, fontSize: ".72rem" }}>{tt("(se agregan los que hagan falta)", "(add as many as needed)")}</span>}
              </label>
            ))}
          </div>
          {sections.payment && (
            <div style={{ marginTop: 8, padding: ".55rem .7rem", borderRadius: 9, background: "rgba(245,158,11,.1)", border: "1px solid rgba(245,158,11,.35)", color: "#b45309", fontSize: ".79rem", lineHeight: 1.45 }}>
              <strong>{tt("Vas a pedir números de tarjeta y de cuenta por un enlace público.", "You're about to ask for card and bank numbers over a public link.")}</strong>{" "}
              {tt("Se guardan cifrados en los métodos de pago del cliente, detrás del PIN, y solo los últimos 4 se ven sin él. El código de seguridad de la tarjeta (CVC) NO se pide ni se guarda: guardarlo está prohibido.",
                  "They're stored encrypted in the client's payment methods, behind the PIN, and only the last 4 show without it. The card security code (CVC) is neither asked for nor stored: keeping it is prohibited.")}
            </div>
          )}
        </div>
        )}

        {/* Aplicación espejo (Lissette 2026-08-19, punto 9). No es una sección
            más: es un interruptor que repite lo del titular para cada persona,
            así que solo tiene sentido si se pide cónyuge o dependientes. */}
        {(sections.spouse || sections.dependents) && (
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, fontSize: ".84rem", color: C.text, background: "var(--ci-bg-04)", border: `1px solid ${C.border}`, borderRadius: 9, padding: ".55rem .7rem", cursor: "pointer" }}>
            <input type="checkbox" checked={!!sections.mirror} onChange={(e) => setSections((sc) => ({ ...sc, mirror: e.target.checked }))} style={{ marginTop: 3 }} />
            <span>
              <strong>{tt("Preguntar lo mismo al cónyuge y a los dependientes", "Ask the spouse and dependents the same")}</strong>
              <span style={{ display: "block", color: C.text2, fontSize: ".78rem", marginTop: 2 }}>
                {tt("Las notas de salud y las preguntas adicionales de abajo se repiten para cada persona, con su nombre delante — no solo para el titular.",
                    "The health notes and the extra questions below are repeated for each person, under their name — not just the main applicant.")}
              </span>
            </span>
          </label>
        )}

        {/* El interruptor puede quedar ENCENDIDO sin repetir nada: solo repite
            las notas de salud y las preguntas adicionales, asi que si la
            plantilla no tiene ninguna de las dos, no hay nada que repetir y el
            formulario sale igual que sin marcarlo.

            Es justo lo que pasa en una plantilla de AUTO (Lissette,
            2026-08-26: *"cuando le doy para tener las mismas preguntas para
            esposos y dependientes, no sale"*): auto no lleva seccion de salud,
            y si todavia no se han escrito preguntas adicionales, marcar la
            casilla no cambia nada — sin que nada lo diga. Se dice aqui, que es
            donde se puede arreglar, y no se apaga sola: apagarla seria
            decidir por el agente que ya escribio "si". */}
        {sections.mirror && !espejoTieneAlgoQueRepetir(sections, fields) && (
          <div style={{ marginTop: 8, padding: ".55rem .7rem", borderRadius: 9, background: "rgba(245,158,11,.1)", border: "1px solid rgba(245,158,11,.35)", color: "#b45309", fontSize: ".79rem", lineHeight: 1.45 }}>
            <strong>{tt("Todavía no hay nada que repetir.", "There is nothing to repeat yet.")}</strong>{" "}
            {tt("Esta casilla repite las notas de salud y las preguntas adicionales. Esta plantilla no pide salud y aún no tiene preguntas adicionales, así que el formulario saldrá igual. Agrega una pregunta abajo y aparecerá bajo el nombre del cónyuge y de cada dependiente.",
                "This box repeats the health notes and the extra questions. This template doesn't ask for health and has no extra questions yet, so the form will look the same. Add a question below and it will appear under the spouse's and each dependent's name.")}
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <label style={labelSt}>{tt("Preguntas adicionales", "Extra questions")}</label>
          <button style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontWeight: 700, fontSize: ".82rem", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={addField}><Plus size={14} /> {tt("Agregar", "Add")}</button>
        </div>
        {fields.map((f, i) => (
          <div key={f.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: ".6rem", marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {/* Varios renglones: las preguntas de salud de Short Term son largas
                  y en una línea no se podían leer (Josef Correia, E-407). Mismo
                  tope que el servidor (MAX_LARGO_DE_PREGUNTA). */}
              <textarea style={{ ...inputSt, flex: "1 1 100%", minWidth: 140, resize: "vertical", fontFamily: "inherit" }}
                rows={Math.min(8, Math.max(1, Math.ceil((f.label || "").length / 70)))} maxLength={2000}
                placeholder={tt("Pregunta", "Question")} value={f.label} onChange={(e) => setField(i, { label: e.target.value })} />
              <select style={{ ...inputSt, flex: 1, minWidth: 110 }} value={f.type} onChange={(e) => setField(i, { type: e.target.value })}>{FIELD_TYPES.map((ty) => <option key={ty} value={ty}>{(FIELD_TYPE_LABELS[ty] || [ty, ty])[lang === "en" ? 1 : 0]}</option>)}</select>
              <label style={{ fontSize: ".78rem", color: C.text2, display: "inline-flex", alignItems: "center", gap: 4 }}><input type="checkbox" checked={!!f.required} onChange={(e) => setField(i, { required: e.target.checked })} /> {tt("Obligatorio", "Required")}</label>
              <button style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer" }} onClick={() => delField(i)}><Trash2 size={15} /></button>
            </div>
            {/* Options input: keep the raw typed string while editing — split(",")
                only, NO trim/filter here, or the comma the user just typed gets
                stripped mid-keystroke and every option collapses into one ("SíNo").
                Trimming/filtering happens at save() time. (Lissette 2026-07-14) */}
            {["select", "radio", "checkbox"].includes(f.type) && (
              <input style={{ ...inputSt, marginTop: 6 }} placeholder={tt("Opciones separadas por coma", "Options separated by comma")} value={(f.options || []).join(",")} onChange={(e) => setField(i, { options: e.target.value.split(",") })} />
            )}
          </div>
        ))}
      </div>

      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: ".86rem", color: C.text, cursor: "pointer" }}>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> <Power size={14} /> {tt("Plantilla activa", "Template active")}
      </label>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
        <div>{!isNew && <button style={{ background: "none", border: "1px solid #ef4444", color: "#fca5a5", borderRadius: 8, padding: ".55rem 1rem", cursor: "pointer", fontSize: ".84rem" }} onClick={remove} disabled={saving}>{tt("Eliminar", "Delete")}</button>}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ padding: ".55rem 1rem", borderRadius: 8, background: "var(--ci-bg-06)", color: C.text, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: ".84rem", display: "inline-flex", alignItems: "center", gap: 6 }} onClick={() => setShowPreview(true)} disabled={saving}><Eye size={15} /> {tt("Vista previa", "Preview")}</button>
          <button style={{ padding: ".55rem 1rem", borderRadius: 8, background: "var(--ci-bg-06)", color: C.text, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: ".84rem" }} onClick={onClose} disabled={saving}>{tt("Cancelar", "Cancel")}</button>
          <button style={{ padding: ".55rem 1.2rem", borderRadius: 8, background: "linear-gradient(135deg,#00d4ff,#00a8c8)", color: "#0a1628", border: "none", cursor: "pointer", fontWeight: 700, fontSize: ".84rem", display: "inline-flex", alignItems: "center", gap: 6 }} onClick={save} disabled={saving}>{saving ? <Loader2 size={15} className="dkt-spin" /> : <Check size={15} />} {tt("Guardar", "Save")}</button>
        </div>
      </div>
      {showPreview && (
        <ApplicationPreview
          template={{ name, policyType, language, intro, sections, customFields: fields.filter((f) => (f.label || "").trim()) }}
          uiLang={lang} C={C} onClose={() => setShowPreview(false)}
        />
      )}
    </Modal>
  );
}

// ── Send modal ──────────────────────────────────────────────────────────
// Multi-recipient + optional email (Lissette 2026-06-22): the agent can add
// several people at once, and a recipient with NO email just yields a link to
// share by hand.
//
// BUSCAR EL CLIENTE EN EL CRM, O ESCRIBIR LOS DATOS (Lissette, punto 73b).
//
// Abierta desde la ficha de un cliente, esta ventana ya venia con su nombre,
// su correo y —lo importante— su `clientId`. Abierta desde el modulo de
// Aplicaciones no venia con nada: el agente escribia el nombre a mano y la
// solicitud salia SIN vincular. Cuando el cliente la llenaba, Deskte no tenia
// contra que ficha archivarla y creaba una NUEVA como prospecto. O sea: al
// cliente que ya existia se le duplicaba la ficha, y los datos que el agente
// ya tenia guardados no se rellenaban solos.
//
// Ahora cada persona de la lista puede salir del CRM. Elegirla rellena su
// nombre y su correo Y la vincula; escribir a mano sigue funcionando igual que
// siempre — hay gente que todavia no es cliente, y esa es justo la que se
// captura con una solicitud.
//
// La vinculacion es POR FILA, no solo la primera: antes solo la primera podia
// llevarla porque era la unica que podia venir de una ficha. Mandando tres
// solicitudes a tres clientes distintos, cada una va a la suya.
function SendModal({ lang, templates, preselect, clientId, defaultName, defaultEmail, clients = [], clientsLoaded = false, clientsServerTotal = null, C, onClose, onSent, onError }) {
  const tt = (es, en) => (lang === "es" ? es : en);
  const [templateId, setTemplateId] = useState(preselect?.id || (templates[0]?.id || ""));
  const [recips, setRecips] = useState([filaDeDestinatario(defaultName, defaultEmail, clientId)]);
  // LA LIBRETA A MEDIO CARGAR ES UNA TRAMPA, y ya mordio una vez: el
  // importador comparaba contra la lista en memoria mientras se estaba
  // cargando, no encontraba a nadie y duplico una cartera entera (E-195).
  // Aqui el dano seria mas callado —el agente busca, no lo encuentra, lo
  // escribe a mano, y la solicitud sale sin vincular—, asi que se avisa.
  const faltanPorCargar = clientsServerTotal != null && !clientsLoaded
    ? Math.max(0, clientsServerTotal - (clients || []).length)
    : 0;
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const inputSt = { width: "100%", padding: ".55rem .7rem", borderRadius: 8, border: `1px solid ${C.border}`, background: "var(--ci-bg-06)", color: C.text, fontSize: ".88rem", boxSizing: "border-box" };
  const labelSt = { fontSize: ".78rem", fontWeight: 600, color: C.text2, display: "block", marginBottom: 4 };

  const setRecip = (i, k, v) => setRecips((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  // La regla vive en destinatariosDeAplicacion.js: aqui solo se enchufa, para
  // que se pueda probar sin montar la pantalla entera.
  const elegirCliente = (i, id) => setRecips((r) => vincularDestinatario(r, i, id, clients));
  const addRecip = () => setRecips((r) => [...r, filaDeDestinatario()]);
  const removeRecip = (i) => setRecips((r) => (r.length > 1 ? r.filter((_, j) => j !== i) : r));
  const copy = (url, idx) => { copyText(url); setCopiedIdx(idx); setTimeout(() => setCopiedIdx(null), 1500); };
  const isEmail = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);

  async function doSend() {
    const valid = destinatariosQueSeEnvian(recips);
    if (valid.length === 0) { onError(tt("Agrega al menos una persona (nombre o correo).", "Add at least one person (name or email).")); return; }
    const badEmail = valid.find((r) => r.email && !isEmail(r.email));
    if (badEmail) { onError(tt(`Correo inválido: ${badEmail.email}`, `Invalid email: ${badEmail.email}`)); return; }
    setSending(true);
    try {
      const out = [];
      for (let i = 0; i < valid.length; i++) {
        const r = valid[i];
        // eslint-disable-next-line no-await-in-loop
        const res = await sendApplication({
          templateId: templateId || undefined,
          // El vinculo es el de SU fila. El de la ficha desde la que se abrio
          // ya viaja dentro de la primera, asi que sobra el `i === 0` de antes
          // — y ademas ahora se puede soltar.
          clientId: r.clientId || undefined,
          recipientName: r.name || undefined,
          recipientEmail: r.email || undefined,
        });
        out.push({ ...res, name: r.name, email: r.email });
      }
      setResults(out);
    } catch (err) { onError(err.message || String(err)); }
    finally { setSending(false); }
  }

  return (
    <Modal onClose={onClose} C={C} title={tt("Enviar aplicación", "Send application")}>
      {results ? (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#34d399", fontWeight: 700 }}><Check size={18} /> {tt(`${results.length} aplicación(es) creada(s).`, `${results.length} application(s) created.`)}</div>
          {results.map((res, idx) => (
            <div key={idx} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: ".7rem .8rem" }}>
              <div style={{ fontWeight: 700, color: C.text, fontSize: ".88rem" }}>{res.name || res.email || tt("Destinatario", "Recipient")}</div>
              <div style={{ fontSize: ".74rem", color: res.emailSent ? "#34d399" : C.text2, marginBottom: 6 }}>
                {res.emailSent ? tt(`✓ Correo enviado a ${res.email}`, `✓ Email sent to ${res.email}`) : tt("Sin correo — comparte el enlace:", "No email — share the link:")}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input readOnly style={inputSt} value={res.url} onFocus={(e) => e.target.select()} />
                <button style={{ ...inputSt, width: "auto", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }} onClick={() => copy(res.url, idx)}>{copiedIdx === idx ? <Check size={15} /> : <Copy size={15} />}</button>
              </div>
            </div>
          ))}
          <div style={{ textAlign: "right", marginTop: 6 }}><button style={{ padding: ".55rem 1.2rem", borderRadius: 8, background: "linear-gradient(135deg,#00d4ff,#00a8c8)", color: "#0a1628", border: "none", cursor: "pointer", fontWeight: 700 }} onClick={onSent}>{tt("Listo", "Done")}</button></div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          <div><label style={labelSt}>{tt("Plantilla", "Template")}</label><select style={inputSt} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>{templates.map((tp) => <option key={tp.id} value={tp.id}>{tp.name} · {tp.language === "en" ? "EN" : "ES"}</option>)}</select></div>
          <div>
            <label style={labelSt}>{tt("Personas", "People")}</label>
            {faltanPorCargar > 0 && (
              <div style={{ marginBottom: 8, padding: ".55rem .7rem", borderRadius: 8, fontSize: ".76rem", lineHeight: 1.45, background: "rgba(255,183,0,.1)", color: "#ffb700", border: "1px solid rgba(255,183,0,.25)" }}>
                {tt(`Todavía se están cargando tus clientes (faltan ${faltanPorCargar}). Si buscas uno y no aparece, espera unos segundos antes de escribirlo a mano — si no, entrará como ficha nueva.`,
                    `Your clients are still loading (${faltanPorCargar} to go). If you search for one and it does not show up, wait a few seconds before typing it by hand — otherwise it comes in as a new record.`)}
              </div>
            )}
            <div style={{ display: "grid", gap: 8 }}>
              {recips.map((r, i) => (
                <div key={i} style={{ display: "grid", gap: 6, border: `1px solid ${r.clientId ? "rgba(52,211,153,.35)" : C.border}`, borderRadius: 10, padding: ".6rem .65rem", background: r.clientId ? "rgba(52,211,153,.05)" : "transparent" }}>
                  {/* Buscar en el CRM. Elegir rellena nombre y correo Y vincula
                      la solicitud a esa ficha; sin vinculo, el cliente que la
                      llena entra como una ficha NUEVA (punto 73b).
                      OJO con el contrato de ClientSearchSelect: avisa con un
                      EVENTO nativo ({target:{value}}), no con el id pelado.
                      Pasarle el evento a elegirCliente dejaba la fila marcada
                      como vinculada pero SIN rellenar nombre ni correo, y al
                      enviar se caia del filtro sin decir nada. Lo cazo el
                      navegador, no el build (regla de oro 130). */}
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ClientSearchSelect
                        clients={clients}
                        value={r.clientId || ""}
                        onChange={(e) => elegirCliente(i, e && e.target ? e.target.value : e)}
                        lang={lang}
                        noneLabel={tt("— No está en el CRM (escribo los datos) —", "— Not in the CRM (I'll type it) —")}
                        placeholder={tt("Buscar cliente por nombre, correo o teléfono…", "Search a client by name, email or phone…")}
                        selectStyle={inputSt}
                      />
                    </div>
                    <button title={tt("Quitar", "Remove")} onClick={() => removeRecip(i)} disabled={recips.length === 1}
                      style={{ background: "var(--ci-bg-06)", color: recips.length === 1 ? C.text2 : "#f87171", border: `1px solid ${C.border}`, borderRadius: 8, cursor: recips.length === 1 ? "default" : "pointer", padding: ".5rem", display: "inline-flex", opacity: recips.length === 1 ? 0.4 : 1 }}><Trash2 size={15} /></button>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input style={{ ...inputSt, flex: 1 }} placeholder={tt("Nombre", "Name")} value={r.name} onChange={(e) => setRecip(i, "name", e.target.value)} />
                    <input type="email" style={{ ...inputSt, flex: 1.3 }} placeholder={tt("Correo (opcional)", "Email (optional)")} value={r.email} onChange={(e) => setRecip(i, "email", e.target.value)} />
                  </div>
                  <div style={{ fontSize: ".7rem", color: r.clientId ? "#34d399" : C.text2 }}>
                    {r.clientId
                      ? tt("✓ Se guardará en la ficha de este cliente.", "✓ It will be filed on this client's record.")
                      : tt("Sin ficha: cuando la llene, entrará al CRM como cliente nuevo.", "No record: when they fill it in, they enter the CRM as a new client.")}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addRecip} style={{ marginTop: 8, padding: ".4rem .8rem", borderRadius: 8, background: "var(--ci-bg-06)", color: C.text, border: `1px dashed ${C.border}`, cursor: "pointer", fontSize: ".82rem", display: "inline-flex", alignItems: "center", gap: 6 }}><Plus size={14} /> {tt("Agregar persona", "Add person")}</button>
            <div style={{ fontSize: ".72rem", color: C.text2, marginTop: 6 }}>{tt("Si no pones correo, se genera el enlace para que lo compartas tú.", "With no email, a link is generated for you to share.")}</div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
            <button style={{ padding: ".55rem 1rem", borderRadius: 8, background: "var(--ci-bg-06)", color: C.text, border: `1px solid ${C.border}`, cursor: "pointer" }} onClick={onClose}>{tt("Cancelar", "Cancel")}</button>
            <button style={{ padding: ".55rem 1.2rem", borderRadius: 8, background: "linear-gradient(135deg,#00d4ff,#00a8c8)", color: "#0a1628", border: "none", cursor: "pointer", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }} onClick={doSend} disabled={sending}>{sending ? <Loader2 size={15} className="dkt-spin" /> : <Send size={15} />} {tt("Enviar", "Send")}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Sent applications list ────────────────────────────────────────────
// El enlace de una solicitud vive 72 HORAS (backend: SENSITIVE_LINK_HOURS).
// Aquí sólo se PINTA esa cuenta atrás; quien decide sigue siendo el servidor.
// Está en la lista porque es lo que hace que el botón de Reenviar se entienda:
// sin ver "Vencido", reenviar parece un botón que duplica correos.
const HORAS_ENLACE = 72;

function SentList({ apps, lang, ptLabel, C, onDelete, canResend = false }) {
  const tt = (es, en) => (lang === "es" ? es : en);
  const [viewing, setViewing] = useState(null); // application id whose submission is open
  const [resending, setResending] = useState("");   // id en curso
  const [resent, setResent] = useState(null);       // { id, url, emailSent, to }
  const [resendError, setResendError] = useState("");
  // Filas retocadas por un reenvío de esta sesión: id → { sentAt, status, viewedAt }.
  //
  // Existe porque el reenvío NO puede volver a pedir la lista al padre: su
  // `refresh()` pone `loading` y eso desmonta esta tabla, con lo que el aviso
  // de "el enlace nuevo se creó pero el correo no salió" moría antes de que
  // nadie lo leyera. El servidor ya devuelve la fila actualizada, así que se
  // aplica aquí y no hace falta ir a buscarla. Al cambiar de pestaña la tabla
  // se vuelve a montar y la verdad vuelve a venir del servidor.
  const [parcheadas, setParcheadas] = useState({});
  const conParche = (a) => ({ ...a, ...(parcheadas[a.id] || {}) });

  // Estado del enlace. Una solicitud ya completada no tiene enlace que vigilar.
  const estadoEnlace = (a) => {
    if (a.submittedAt || a.status === "SUBMITTED") return null;
    // Anulado gana sobre la fecha: da igual cuánto le quedaba.
    if (a.tokenRevokedAt) return { vencido: true, texto: tt("Anulado", "Revoked") };
    const base = a.sentAt || a.createdAt;
    if (!base) return null;
    const restanMs = new Date(base).getTime() + HORAS_ENLACE * 3600 * 1000 - Date.now();
    if (restanMs <= 0) return { vencido: true, texto: tt("Vencido", "Expired") };
    const horas = Math.ceil(restanMs / 3600000);
    return {
      vencido: false,
      texto: horas >= 24
        ? tt(`Vence en ${Math.ceil(horas / 24)} d`, `${Math.ceil(horas / 24)}d left`)
        : tt(`Vence en ${horas} h`, `${horas}h left`),
    };
  };

  const anular = async (a) => {
    const quien = a.recipientName || a.recipientEmail || tt("este destinatario", "this recipient");
    const ok = window.confirm(tt(
      `¿Anular el enlace de ${quien}?\n\nDejará de abrir al instante. Si hace falta, después puedes reenviarle uno nuevo.`,
      `Revoke ${quien}'s link?\n\nIt stops opening immediately. You can resend a new one afterwards if needed.`,
    ));
    if (!ok) return;
    setResendError(""); setResent(null); setResending(a.id);
    try {
      const d = await revokeApplication(a.id);
      const fila = d?.application || {};
      setParcheadas((p) => ({
        ...p,
        [a.id]: { ...(p[a.id] || {}), tokenRevokedAt: fila.tokenRevokedAt || new Date().toISOString() },
      }));
    } catch (e) {
      setResendError(e.message || String(e));
    } finally {
      setResending("");
    }
  };

  const reenviar = async (a) => {
    const quien = a.recipientName || a.recipientEmail || tt("este destinatario", "this recipient");
    // Se confirma porque tiene un efecto que no se ve: el enlace anterior deja
    // de funcionar en ese instante. Callarlo haría que el agente creyera que
    // los dos siguen sirviendo.
    const ok = window.confirm(tt(
      `¿Enviar un enlace nuevo a ${quien}?\n\nEl enlace anterior dejará de funcionar y el nuevo durará ${HORAS_ENLACE} horas.`,
      `Send a new link to ${quien}?\n\nThe previous link will stop working, and the new one lasts ${HORAS_ENLACE} hours.`,
    ));
    if (!ok) return;
    setResendError(""); setResent(null); setResending(a.id);
    try {
      const d = await resendApplication(a.id);
      const fila = d?.application || {};
      setParcheadas((p) => ({
        ...p,
        [a.id]: {
          sentAt: fila.sentAt || new Date().toISOString(),
          status: fila.status || "SENT",
          viewedAt: null,
          // Reenviar revive el enlace: si quedara la marca de anulado, la fila
          // diría "Anulado" mientras el enlace nuevo funciona.
          tokenRevokedAt: null,
        },
      }));
      setResent({ id: a.id, url: d?.url || "", emailSent: !!d?.emailSent, to: a.recipientEmail || "" });
    } catch (e) {
      setResendError(e.message || String(e));
    } finally {
      setResending("");
    }
  };

  if (apps.length === 0) return <div style={{ textAlign: "center", padding: "2.5rem", color: C.text2 }}>{tt("No has enviado aplicaciones todavía.", "No applications sent yet.")}</div>;
  const statusColor = (s) => (s === "SUBMITTED" ? "#34d399" : s === "VIEWED" ? "#fbbf24" : C.text2);
  const statusLabel = (s) => (s === "SUBMITTED" ? tt("Completada", "Submitted") : s === "VIEWED" ? tt("Vista", "Viewed") : tt("Enviada", "Sent"));
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-US") : "—");
  const del = (a) => {
    if (window.confirm(tt(`¿Eliminar la aplicación de ${a.recipientName || a.recipientEmail || "este destinatario"}? No se puede deshacer.`, `Delete the application for ${a.recipientName || a.recipientEmail || "this recipient"}? This cannot be undone.`))) onDelete(a.id);
  };
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".86rem" }}>
        <thead><tr style={{ color: C.text2, textAlign: "left" }}>
          {[tt("Destinatario", "Recipient"), tt("Tipo", "Type"), tt("Estado", "Status"), tt("Enviada", "Sent"), tt("Vista", "Viewed"), tt("Completada", "Submitted"), tt("Enlace", "Link"), ""].map((h, i) => <th key={i} style={{ padding: ".5rem .6rem", borderBottom: `1px solid ${C.border}`, fontWeight: 600 }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {apps.map(conParche).map((a) => (
            <tr key={a.id} style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: ".55rem .6rem", color: C.text }}>{a.recipientName || a.recipientEmail || "—"}<div style={{ fontSize: ".72rem", color: C.text2 }}>{a.recipientEmail}</div></td>
              <td style={{ padding: ".55rem .6rem", color: C.text2 }}>{ptLabel(a.policyType)}</td>
              <td style={{ padding: ".55rem .6rem", color: statusColor(a.status), fontWeight: 700 }}>{statusLabel(a.status)}</td>
              <td style={{ padding: ".55rem .6rem", color: C.text2 }}>{fmt(a.sentAt || a.createdAt)}</td>
              <td style={{ padding: ".55rem .6rem", color: C.text2 }}>{fmt(a.viewedAt)}</td>
              <td style={{ padding: ".55rem .6rem", color: C.text2 }}>{fmt(a.submittedAt)}</td>
              <td style={{ padding: ".55rem .6rem", whiteSpace: "nowrap" }}>
                {(() => {
                  const e = estadoEnlace(a);
                  if (!e) return <span style={{ color: C.text2 }}>—</span>;
                  return <span style={{ color: e.vencido ? "#f87171" : C.text2, fontWeight: e.vencido ? 700 : 400 }}>{e.texto}</span>;
                })()}
              </td>
              <td style={{ padding: ".55rem .6rem", textAlign: "right", whiteSpace: "nowrap" }}>
                {a.status === "SUBMITTED" && (
                  <button type="button" onClick={() => setViewing(a.id)} title={tt("Ver aplicación", "View application")} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", padding: 4 }}><Eye size={15} /></button>
                )}
                {canResend && estadoEnlace(a) && (
                  <button
                    type="button"
                    onClick={() => reenviar(a)}
                    disabled={resending === a.id}
                    title={tt("Enviar un enlace nuevo (el anterior deja de servir)", "Send a new link (the previous one stops working)")}
                    style={{ background: "none", border: "none", color: C.accent, cursor: resending === a.id ? "default" : "pointer", padding: 4, opacity: resending === a.id ? 0.5 : 1 }}
                  >
                    {resending === a.id ? <Loader2 size={15} className="dkt-spin" /> : <RefreshCw size={15} />}
                  </button>
                )}
                {canResend && estadoEnlace(a) && !a.tokenRevokedAt && (
                  <button
                    type="button"
                    onClick={() => anular(a)}
                    disabled={resending === a.id}
                    title={tt("Anular el enlace (deja de abrir al instante)", "Revoke the link (it stops opening immediately)")}
                    style={{ background: "none", border: "none", color: C.text2, cursor: resending === a.id ? "default" : "pointer", padding: 4, opacity: resending === a.id ? 0.5 : 1 }}
                  >
                    <Ban size={15} />
                  </button>
                )}
                {onDelete && <button type="button" onClick={() => del(a)} title={tt("Eliminar", "Delete")} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", padding: 4 }}><Trash2 size={15} /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {resendError && (
        <div style={{ marginTop: ".7rem", color: "#f87171", fontSize: ".82rem", display: "flex", alignItems: "center", gap: 6 }}>
          <AlertCircle size={14} />{resendError}
        </div>
      )}
      {resent && (
        <div style={{ marginTop: ".7rem", padding: ".7rem .85rem", border: `1px solid ${C.border}`, borderRadius: 8, background: "var(--ci-bg-06)", fontSize: ".82rem", color: C.text }}>
          {resent.emailSent
            ? <span><Check size={14} style={{ verticalAlign: "-2px" }} /> {tt(`Enlace nuevo enviado a ${resent.to}. Dura ${HORAS_ENLACE} horas y el anterior ya no funciona.`, `New link sent to ${resent.to}. It lasts ${HORAS_ENLACE} hours and the previous one no longer works.`)}</span>
            /* Sin buzón conectado el correo NO sale. Decirlo y dar el enlace es
               la diferencia entre "no pasó nada" y "compártelo tú". */
            : <span style={{ color: "#fbbf24" }}><AlertCircle size={14} style={{ verticalAlign: "-2px" }} /> {tt("El enlace nuevo se creó, pero el correo no salió (no hay buzón conectado). Cópialo y compártelo tú:", "The new link was created, but the email didn't go out (no mailbox connected). Copy it and share it yourself:")}</span>}
          {(!resent.emailSent && resent.url) && (
            <div style={{ display: "flex", gap: 6, marginTop: ".5rem" }}>
              <input readOnly value={resent.url} onFocus={(e) => e.target.select()} style={{ flex: 1, background: "var(--ci-bg-06)", color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: ".45rem .6rem", fontSize: ".8rem" }} />
              <button type="button" onClick={() => copyText(resent.url)} style={{ background: "var(--ci-bg-06)", color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer", padding: ".45rem .6rem" }}><Copy size={14} /></button>
            </div>
          )}
        </div>
      )}
      {viewing && <SubmissionModal appId={viewing} lang={lang} ptLabel={ptLabel} C={C} onClose={() => setViewing(null)} />}
    </div>
  );
}

// ── Submission viewer: the answers exactly as the client filled them ─────
function SubmissionModal({ appId, lang, ptLabel, C, onClose }) {
  const tt = (es, en) => (lang === "es" ? es : en);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError("");
      try { const d = await getApplicationSubmission(appId); if (alive) setData(d); }
      catch (e) { if (alive) setError(e.message || String(e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [appId]);

  const a = data?.answers || null;
  const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-US") : "—");
  const val = (v) => { const s = (v === undefined || v === null) ? "" : String(v).trim(); return s || "—"; };
  const labelSt = { fontSize: ".72rem", fontWeight: 600, color: C.text2, textTransform: "uppercase", letterSpacing: ".02em" };
  const valSt = { fontSize: ".9rem", color: C.text, marginTop: 2, wordBreak: "break-word" };

  const Field = ({ label, value }) => (
    <div><div style={labelSt}>{label}</div><div style={valSt}>{val(value)}</div></div>
  );
  const Section = ({ title, children }) => (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: ".82rem", fontWeight: 700, color: C.accent, marginBottom: 8, borderBottom: `1px solid ${C.border}`, paddingBottom: 4 }}>{title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px" }}>{children}</div>
    </div>
  );

  const fullAddress = a ? [a.address, a.city, a.state, a.zipCode].map((x) => (x || "").trim()).filter(Boolean).join(", ") : "";
  const person = (p, keys) => keys.map((k) => p?.[k]).some((x) => (x || "").toString().trim());
  const customFields = Array.isArray(data?.customFields) ? data.customFields : [];
  const customValues = (a && a.customValues) || {};

  // Respuestas del espejo: las mismas preguntas del titular contestadas por el
  // cónyuge o por un dependiente, guardadas con la persona por delante
  // ("spouse.cf_3", "dep.0.cf_3"). Se pintan solo si esa persona contestó algo.
  const respuestasDe = (quien) => {
    const filas = [];
    const salud = customValues[`${quien}.healthNotes`];
    if ((salud || "").trim()) filas.push({ id: `${quien}.healthNotes`, label: tt("Notas de salud", "Health notes"), value: salud });
    customFields.forEach((f) => {
      const v = customValues[`${quien}.${f.id}`];
      if ((v || "").toString().trim()) filas.push({ id: `${quien}.${f.id}`, label: f.label || f.id, value: v });
    });
    return filas;
  };
  const Respuestas = ({ filas }) => (filas.length ? (
    <div style={{ gridColumn: "1 / -1", marginTop: 6, borderTop: `1px dashed ${C.border}`, paddingTop: 6, display: "grid", gap: 6 }}>
      {filas.map((f) => <Field key={f.id} label={f.label} value={f.value} />)}
    </div>
  ) : null);

  return (
    <Modal onClose={onClose} C={C} title={tt("Aplicación enviada", "Submitted application")}>
      {loading ? (
        <div style={{ textAlign: "center", padding: "2rem", color: C.text2 }}><Loader2 size={18} className="dkt-spin" /> {tt("Cargando…", "Loading…")}</div>
      ) : error ? (
        <div style={{ background: "#3a1a1a", border: "1px solid #ef4444", borderRadius: 10, padding: ".7rem 1rem", color: "#fca5a5", fontSize: ".86rem" }}>{error}</div>
      ) : !a ? (
        <div style={{ textAlign: "center", padding: "2rem", color: C.text2 }}>{tt("Esta aplicación aún no ha sido completada por el cliente.", "This application has not been completed by the client yet.")}</div>
      ) : (
        <div>
          <div style={{ fontSize: ".78rem", color: C.text2 }}>
            {data.title ? <span>{data.title} · </span> : null}{ptLabel(data.policyType)}
            <span> · {tt("Completada", "Submitted")}: {fmtDate(data.submittedAt)}</span>
          </div>

          <Section title={tt("Datos personales", "Personal details")}>
            <Field label={tt("Nombre", "First name")} value={a.firstName} />
            <Field label={tt("Apellido", "Last name")} value={a.lastName} />
            <Field label={tt("Correo", "Email")} value={a.email} />
            <Field label={tt("Teléfono", "Phone")} value={a.phone} />
            <Field label={tt("Fecha de nacimiento", "Date of birth")} value={a.dateOfBirth} />
            <Field label="SSN" value={a.ssn} />
            <Field label={tt("Estado civil", "Marital status")} value={a.maritalStatus} />
            <Field label={tt("Estatus migratorio", "Immigration status")} value={a.immigrationStatus} />
            <Field label={tt("Empleador", "Employer")} value={a.employer} />
            <Field label={tt("Ingreso anual", "Annual income")} value={a.annualIncome} />
            <div style={{ gridColumn: "1 / -1" }}><Field label={tt("Dirección", "Address")} value={fullAddress} /></div>
          </Section>

          {a.spouse && person(a.spouse, ["firstName", "lastName", "dateOfBirth", "email", "phone"]) && (
            <Section title={tt("Cónyuge", "Spouse")}>
              <Field label={tt("Nombre", "First name")} value={a.spouse.firstName} />
              <Field label={tt("Apellido", "Last name")} value={a.spouse.lastName} />
              <Field label={tt("Fecha de nacimiento", "Date of birth")} value={a.spouse.dateOfBirth} />
              <Field label="SSN" value={a.spouse.ssn} />
              <Field label={tt("Correo", "Email")} value={a.spouse.email} />
              <Field label={tt("Teléfono", "Phone")} value={a.spouse.phone} />
              <Field label={tt("Empleador", "Employer")} value={a.spouse.employer} />
              <Field label={tt("Ingreso anual", "Annual income")} value={a.spouse.annualIncome} />
              <Respuestas filas={respuestasDe("spouse")} />
            </Section>
          )}

          {Array.isArray(a.dependents) && a.dependents.filter((d) => person(d, ["firstName", "lastName", "dateOfBirth"])).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: ".82rem", fontWeight: 700, color: C.accent, marginBottom: 8, borderBottom: `1px solid ${C.border}`, paddingBottom: 4 }}>{tt("Dependientes", "Dependents")}</div>
              <div style={{ display: "grid", gap: 8 }}>
                {/* Se recorre la lista COMPLETA y se salta dentro, para no perder
                    la correspondencia con el índice: las respuestas del espejo
                    se guardaron como dep.0.*, dep.1.* sobre esta misma lista. */}
                {a.dependents.map((d, i) => {
                  if (!person(d, ["firstName", "lastName", "dateOfBirth"])) return null;
                  const filas = respuestasDe(`dep.${i}`);
                  return (
                    <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: ".55rem .7rem", fontSize: ".86rem", color: C.text }}>
                      <strong>{`${d.firstName || ""} ${d.lastName || ""}`.trim() || tt("Dependiente", "Dependent")}</strong>
                      <span style={{ color: C.text2 }}>
                        {d.relation ? ` · ${d.relation}` : ""}{d.dateOfBirth ? ` · ${d.dateOfBirth}` : ""}{d.immigrationStatus ? ` · ${d.immigrationStatus}` : ""}
                      </span>
                      {filas.length > 0 && (
                        <div style={{ marginTop: 6, borderTop: `1px dashed ${C.border}`, paddingTop: 6, display: "grid", gap: 6 }}>
                          {filas.map((f) => <Field key={f.id} label={f.label} value={f.value} />)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* LAS RESPUESTAS DEL FORMULARIO DE AUTO (punto 69). Del mismo
              catalogo que las pregunto, asi que la etiqueta que lee la agente
              es palabra por palabra la que leyo el cliente. Un bloque que el
              cliente dejo en blanco no se pinta: una pantalla llena de "—" no
              informa de nada.

              Los numeros de tarjeta y de cuenta llegan aqui ya enmascarados
              (•••• 1234): los completos estan en los metodos de pago del
              cliente, detras del PIN. */}
          {SECCIONES_DE_AUTO.map((b) => {
            const dato = (a.auto || {})[
              { autoDriver: 'conductor', vehicles: 'carros', autoDrivers: 'conductores',
                previousAddresses: 'direcciones', currentInsurance: 'seguroActual',
                autoPolicy: 'poliza', payment: 'pago' }[b.key]
            ];
            const filas = Array.isArray(dato) ? dato : (dato ? [dato] : []);
            // Los campos de HOY mas los RETIRADOS. Una aplicacion enviada
            // antes de que se quitara un campo lo trae escrito, y esta
            // pantalla pinta desde el catalogo: sin esto, ese dato se queda
            // guardado en la base y desaparece de la vista de quien la abre.
            const verCampos = camposParaVer(b);
            const conAlgo = filas.filter((f) => verCampos.some((cp) => String(f?.[cp.key] ?? '').trim()));
            if (!conAlgo.length) return null;
            return (
              <Section key={b.key} title={etiqueta(b, lang)}>
                {conAlgo.map((fila, i) => (
                  <React.Fragment key={i}>
                    {b.repetidor && (
                      <div style={{ gridColumn: "1 / -1", fontSize: ".74rem", fontWeight: 700, color: C.text2, marginTop: i ? 8 : 0 }}>
                        {etiqueta(b, lang)} {i + 1}
                      </div>
                    )}
                    {verCampos
                      .filter((cp) => String(fila?.[cp.key] ?? '').trim())
                      .map((cp) => <Field key={cp.key} label={etiqueta(cp, lang)} value={fila[cp.key]} />)}
                  </React.Fragment>
                ))}
              </Section>
            );
          })}

          {(a.healthNotes || "").trim() && (
            <Section title={tt("Salud", "Health")}>
              <div style={{ gridColumn: "1 / -1" }}><Field label={tt("Notas de salud", "Health notes")} value={a.healthNotes} /></div>
            </Section>
          )}

          {customFields.length > 0 && (
            <Section title={tt("Preguntas adicionales", "Extra questions")}>
              {customFields.map((f) => (
                <div key={f.id} style={{ gridColumn: "1 / -1" }}><Field label={f.label || f.id} value={customValues[f.id]} /></div>
              ))}
            </Section>
          )}

          <div style={{ textAlign: "right", marginTop: 20 }}>
            <button style={{ padding: ".55rem 1.2rem", borderRadius: 8, background: "var(--ci-bg-06)", color: C.text, border: `1px solid ${C.border}`, cursor: "pointer", fontWeight: 600 }} onClick={onClose}>{tt("Cerrar", "Close")}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Client ficha tab: this client's applications + send ─────────────────
export function ClientApplicationsTab({ client, lang = "es" }) {
  const tt = (es, en) => (lang === "es" ? es : en);
  const [templates, setTemplates] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showSend, setShowSend] = useState(false);
  const C = { text: "var(--c-white,#eef6ff)", text2: "var(--c-text2,#7aa3c0)", card: "var(--ci-bg-04)", border: "var(--ci-bd-20)", accent: "#00d4ff" };
  const ptLabel = (pt) => (POLICY_TYPES.find(([v]) => v === (pt || ""))?.[1]?.[lang]) || pt || tt("General", "General");

  async function refresh() {
    setLoading(true); setError("");
    try {
      const [tpls, list] = await Promise.all([listApplicationTemplates(), listApplications(client.id)]);
      setTemplates(tpls); setApps(list);
    } catch (err) { setError(err.message || String(err)); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, [client.id]);

  const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim();
  return (
    <div style={{ color: C.text }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: ".82rem", color: C.text2 }}>{tt("Envíale un formulario de aplicación seguro. Al completarlo, sus datos se actualizan aquí.", "Send a secure application form. When completed, their details update here.")}</div>
        <button onClick={() => setShowSend(true)} style={{ padding: ".55rem 1rem", borderRadius: 10, background: "linear-gradient(135deg,#00d4ff,#00a8c8)", color: "#0a1628", border: "none", cursor: "pointer", fontWeight: 700, fontSize: ".84rem", display: "inline-flex", alignItems: "center", gap: 6 }}><Send size={15} /> {tt("Enviar aplicación", "Send application")}</button>
      </div>
      {error && <div style={{ background: "#3a1a1a", border: "1px solid #ef4444", borderRadius: 10, padding: ".7rem 1rem", marginBottom: ".75rem", color: "#fca5a5", fontSize: ".86rem" }}>{error}</div>}
      {loading ? (
        <div style={{ textAlign: "center", padding: "2rem", color: C.text2 }}><Loader2 size={18} className="dkt-spin" /> {tt("Cargando…", "Loading…")}</div>
      ) : (
        <SentList apps={apps} lang={lang} ptLabel={ptLabel} C={C} canResend onDelete={async (id) => { try { await deleteApplication(id); await refresh(); } catch (e) { setError(e.message || String(e)); } }} />
      )}
      {showSend && (
        <SendModal lang={lang} templates={templates} clientId={client.id} defaultName={fullName} defaultEmail={client.email || ""} C={C}
          onClose={() => setShowSend(false)} onSent={async () => { setShowSend(false); await refresh(); }} onError={setError} />
      )}
    </div>
  );
}

// ── Modal shell ───────────────────────────────────────────────────────
function Modal({ title, children, onClose, C }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(2,8,20,.7)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "4vh 1rem", zIndex: 1000, overflowY: "auto" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--c-surface,#0d1c2e)", border: `1px solid ${C.border}`, borderRadius: 14, padding: "1.4rem", width: "100%", maxWidth: 620, boxShadow: "0 20px 60px rgba(0,0,0,.5)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: "1.1rem", color: C.text }}>{title}</h3>
          <button style={{ background: "none", border: "none", color: C.text2, cursor: "pointer" }} onClick={onClose}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Application preview ───────────────────────────────────────────────────
// A read-only render of the blank form the way the CLIENT will see it, given
// the template's enabled `sections` + `customFields`. Labels follow the
// template's own language (`formLang`), NOT the agent's UI language, because
// that's what the recipient gets. `uiLang` only controls the wrapper chrome
// (banner / close button). Lissette 2026-07-13: "quiero previsualizar la
// aplicación antes de enviarla".
function ApplicationPreview({ template, uiLang, C, onClose }) {
  const tt = (es, en) => (uiLang === "es" ? es : en);
  const fl = template.language === "en" ? "en" : "es";
  const L = (es, en) => (fl === "es" ? es : en);
  const sec = template.sections || {};
  const customFields = Array.isArray(template.customFields) ? template.customFields : [];

  const fieldSt = { width: "100%", padding: ".5rem .65rem", borderRadius: 8, border: `1px solid ${C.border}`, background: "var(--ci-bg-04)", color: C.text2, fontSize: ".84rem", boxSizing: "border-box", pointerEvents: "none" };
  const lblSt = { fontSize: ".76rem", fontWeight: 600, color: C.text2, display: "block", marginBottom: 4 };
  const grpTitle = { fontSize: ".82rem", fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: ".03em", margin: "16px 0 8px" };
  const Field = ({ label, req, ph, wide, type }) => (
    <div style={{ gridColumn: wide ? "1 / -1" : "auto" }}>
      <label style={lblSt}>{label}{req && <span style={{ color: "#f87171" }}> *</span>}</label>
      {type === "textarea"
        ? <div style={{ ...fieldSt, minHeight: 54, color: "var(--c-text3,#4a6a88)" }}>{ph || ""}</div>
        : <div style={fieldSt}>{ph || " "}</div>}
    </div>
  );
  const Group = ({ title, children }) => (
    <div><div style={grpTitle}>{title}</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>{children}</div></div>
  );

  return (
    <Modal onClose={onClose} C={C} title={tt("Vista previa de la aplicación", "Application preview")}>
      <div style={{ background: "rgba(0,212,255,.08)", border: `1px solid ${C.border}`, borderRadius: 10, padding: ".6rem .8rem", fontSize: ".8rem", color: C.text2, marginBottom: 14 }}>
        👁 {tt("Así verá el cliente el formulario. Es solo lectura — no se puede escribir aquí.",
              "This is how the client will see the form. It's read-only — you can't type here.")}
        <span style={{ marginLeft: 6, fontWeight: 700 }}>· {L("Español", "English")}</span>
      </div>

      <div style={{ pointerEvents: "none", userSelect: "none", opacity: .96 }}>
        {template.intro && (
          <div style={{ background: "var(--ci-bg-04)", border: `1px solid ${C.border}`, borderRadius: 10, padding: ".8rem 1rem", fontSize: ".86rem", color: C.text, marginBottom: 6, whiteSpace: "pre-wrap" }}>{template.intro}</div>
        )}

        <Group title={L("Datos personales", "Personal information")}>
          <Field label={L("Nombre", "First name")} req />
          <Field label={L("Apellido", "Last name")} req />
          <Field label={L("Correo electrónico", "Email")} />
          <Field label={L("Teléfono", "Phone")} />
        </Group>

        {sec.address && (
          <Group title={L("Dirección", "Address")}>
            <Field label={L("Dirección", "Street address")} wide />
            <Field label={L("Ciudad", "City")} />
            <Field label={L("Estado", "State")} />
            <Field label={L("Código postal", "ZIP code")} />
            {/* Lo de la vivienda se pregunta aqui, con la direccion
                (Lissette 2026-09-09), no treinta preguntas mas abajo. */}
            {sec.autoDriver && viviendaVaConLaDireccion(sec) &&
              CAMPOS_CON_LA_DIRECCION.map((cp) => <Field key={cp.key} label={etiqueta(cp, fl)} />)}
          </Group>
        )}

        {(sec.dob || sec.ssn) && (
          <Group title={L("Identificación", "Identification")}>
            {sec.dob && <Field label={L("Fecha de nacimiento", "Date of birth")} />}
            {sec.ssn && <Field label={L("Número de Seguro Social (SSN)", "Social Security Number (SSN)")} ph="•••-••-••••" />}
          </Group>
        )}

        {sec.employment && (
          <Group title={L("Empleo y estado", "Employment & status")}>
            <Field label={L("Empleador", "Employer")} />
            <Field label={L("Ingreso anual", "Annual income")} />
            <Field label={L("Estado civil", "Marital status")} />
            <Field label={L("Estatus migratorio", "Immigration status")} />
          </Group>
        )}

        {sec.spouse && (
          <Group title={L("Cónyuge", "Spouse")}>
            <Field label={L("Nombre del cónyuge", "Spouse first name")} />
            <Field label={L("Apellido del cónyuge", "Spouse last name")} />
            {/* Al conyuge y a los dependientes se les pide lo MISMO que al
                titular: si la fecha o el SSN estan apagados arriba, tampoco se
                les piden a ellos (Lissette 2026-09-04, punto 68). Antes salian
                siempre, asi que apagar la casilla del titular no servia de
                nada — el formulario seguia pidiendo el numero, solo que a otra
                persona. */}
            {sec.dob && <Field label={L("Fecha de nacimiento", "Date of birth")} />}
            {sec.ssn && <Field label={L("SSN del cónyuge", "Spouse SSN")} ph="•••-••-••••" />}
            {sec.mirror && espejoTieneAlgoQueRepetir(sec, customFields) && (
              <div style={{ gridColumn: "1 / -1", fontSize: ".76rem", color: C.text2, background: "rgba(0,212,255,.07)", border: `1px solid ${C.border}`, borderRadius: 8, padding: ".45rem .6rem" }}>
                🔁 {L("Y las mismas preguntas del titular, bajo el nombre del cónyuge.",
                      "Plus the same questions as the main applicant, under the spouse's name.")}
              </div>
            )}
          </Group>
        )}

        {sec.dependents && (
          <div>
            <div style={grpTitle}>{L("Dependientes", "Dependents")}</div>
            <div style={{ ...fieldSt, minHeight: 40, display: "flex", alignItems: "center", color: "var(--c-text3,#4a6a88)", fontSize: ".8rem" }}>
              ➕ {L(`El cliente podrá agregar dependientes (nombre, parentesco${sec.dob ? ", fecha de nacimiento" : ""}${sec.ssn ? ", SSN" : ""}).`,
                    `The client can add dependents (name, relationship${sec.dob ? ", date of birth" : ""}${sec.ssn ? ", SSN" : ""}).`)}
            </div>
            {sec.mirror && espejoTieneAlgoQueRepetir(sec, customFields) && (
              <div style={{ marginTop: 6, fontSize: ".76rem", color: C.text2, background: "rgba(0,212,255,.07)", border: `1px solid ${C.border}`, borderRadius: 8, padding: ".45rem .6rem" }}>
                🔁 {L("A cada dependiente se le harán además las mismas preguntas del titular, bajo su nombre.",
                      "Each dependent is also asked the same questions as the main applicant, under their name.")}
              </div>
            )}
          </div>
        )}

        {/* Los bloques de auto en la vista previa, del mismo catalogo. */}
        {SECCIONES_DE_AUTO.filter((b) => sec[b.key]).map((b) => (
          <Group key={b.key} title={etiqueta(b, fl)}>
            {[...b.campos, ...(viviendaVaConLaDireccion(sec) ? [] : (b.enDireccion || []))]
              .map((cp) => <Field key={cp.key} label={etiqueta(cp, fl)} />)}
            {b.repetidor && (
              <div style={{ gridColumn: "1 / -1", fontSize: ".76rem", color: C.text2 }}>
                ➕ {L("El cliente puede agregar los que hagan falta.", "The client can add as many as needed.")}
              </div>
            )}
          </Group>
        ))}

        {sec.health && (
          <div>
            <div style={grpTitle}>{L("Salud", "Health")}</div>
            <Field label={L("Notas de salud / condiciones", "Health notes / conditions")} type="textarea" wide />
          </div>
        )}

        {customFields.length > 0 && (
          <div>
            <div style={grpTitle}>{L("Preguntas adicionales", "Extra questions")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {customFields.map((f) => (
                <Field key={f.id} label={f.label} req={f.required} wide={f.type === "textarea" || (f.label || "").length > 80}
                  type={f.type === "textarea" ? "textarea" : "text"}
                  ph={f.type === "yesno"
                    ? `☐ ${L("Sí", "Yes")}    ☐ ${L("No", "No")}`
                    : (["select", "radio", "checkbox"].includes(f.type) && (f.options || []).length
                      ? (f.type === "checkbox" ? "☐ " : "▾ ") + (f.options || []).map((s) => s.trim()).filter(Boolean).join(" · ")
                      : "")} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
        <button style={{ padding: ".55rem 1.2rem", borderRadius: 8, background: "var(--ci-bg-06)", color: C.text, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: ".84rem" }} onClick={onClose}>{tt("Cerrar", "Close")}</button>
      </div>
    </Modal>
  );
}
